import { useEffect, useMemo, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

import { AssetTreeState, getAssetTreeSelection } from '@/configuration/assetTree/assetTreeSlice';
import { useAppSelector } from '@/configuration/setup/hooks';
import { mapCriticalityToStatus } from '@/features/brakes/mapper/mapCriticalityToStatus';
import { GetBrakesRequestParams, useGetGroupedBrakesQuery } from '@/services/vehicleDiagnosticsApi';
import { useBrakesQueryParams } from './useBrakesQueryParams';

export const useBrakesRequestData = (initialParams: Partial<GetBrakesRequestParams>) => {
    const { workshopIds, assetIds, accountIds, initialized }: AssetTreeState = useAppSelector(getAssetTreeSelection);
    const params = useBrakesQueryParams({ ...initialParams, workshopIds, assetIds, accountIds });
    const [shouldSkipFetching, setShouldSkipFetching] = useState(true);

    useEffect(() => {
        if (initialized) {
            params.setAssetIds(assetIds);
            params.setWorkshopIds(workshopIds);
            params.setAccountIds(accountIds);
            params.pagination.setPageNumber(0);
            setShouldSkipFetching(false);
        }
    }, [assetIds, workshopIds, accountIds, initialized]);

    const queryParams = useMemo(
        () => ({
            pageSize: params.pagination.pageSize,
            pageNumber: params.pagination.pageNumber,
            sortBy: params.sorting.sortBy,
            sortDirection: params.sorting.sortDirection,
            assetIds: params.assetIds,
            workshopIds: params.workshopIds,
            accountIds: params.accountIds,
            min: params.min,
            max: params.max,
            status: mapCriticalityToStatus(params.criticalityStatus),
        }),
        [
            params.pagination.pageSize,
            params.pagination.pageNumber,
            params.sorting.sortBy,
            params.sorting.sortDirection,
            params.assetIds,
            params.workshopIds,
            params.accountIds,
            params.min,
            params.max,
            params.criticalityStatus,
        ],
    );
    const query = useGetGroupedBrakesQuery(shouldSkipFetching ? skipToken : queryParams);

    return { ...params, ...query };
};
